import React from 'react';
import { motion } from 'framer-motion';
import { 
  ShieldCheckIcon, 
  UsersIcon, 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../../contexts/LanguageContext';

const MudadCompliance: React.FC = () => {
  const { t } = useLanguage();

  // Mock data - في التطبيق الحقيقي سيأتي من API
  const mudadData = {
    status: 'connected',
    employeesRegistered: 145,
    complianceRate: 98.5,
    lastSync: '2024-01-06T23:45:00',
    alerts: [
      {
        id: 1,
        type: 'warning',
        message: 'تأخير في رفع كشف الرواتب لشهر ديسمبر',
        priority: 'medium',
        dueDate: '2024-01-10'
      },
      {
        id: 2,
        type: 'info',
        message: 'تم تحديث بيانات 3 موظفين جدد',
        priority: 'low',
        dueDate: null
      }
    ],
    zatcaStatus: {
      connected: true,
      invoicesIssued: 1247,
      complianceRate: 100
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected': return 'text-green-500';
      case 'warning': return 'text-yellow-500';
      case 'error': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  const getStatusBg = (status: string) => {
    switch (status) {
      case 'connected': return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
      case 'warning': return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800';
      case 'error': return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
      default: return 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 }}
      className="space-y-6"
    >
      {/* Mudad Status Card */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg">
              <UsersIcon className="w-6 h-6 text-white" />
            </div>
            <div className="ml-4 rtl:ml-0 rtl:mr-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {t('dashboard.hr_mudad')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                نظام حماية الأجور السعودي
              </p>
            </div>
          </div>
          <div className={`flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor('connected')}`}>
            <CheckCircleIcon className="w-4 h-4 ml-1 rtl:ml-0 rtl:mr-1" />
            {t('dashboard.mudad_connected')}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('dashboard.mudad_employees')}
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {mudadData.employeesRegistered}
                </p>
              </div>
              <UsersIcon className="w-8 h-8 text-green-500" />
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('dashboard.mudad_compliance')}
                </p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {mudadData.complianceRate}%
                </p>
              </div>
              <ShieldCheckIcon className="w-8 h-8 text-green-500" />
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('dashboard.last_sync')}
                </p>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  منذ 15 دقيقة
                </p>
              </div>
              <ClockIcon className="w-8 h-8 text-blue-500" />
            </div>
          </div>
        </div>
      </div>

      {/* ZATCA Integration */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center shadow-lg">
              <DocumentTextIcon className="w-6 h-6 text-white" />
            </div>
            <div className="ml-4 rtl:ml-0 rtl:mr-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {t('dashboard.zatca_status')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                الفوترة الإلكترونية
              </p>
            </div>
          </div>
          <div className={`flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor('connected')}`}>
            <CheckCircleIcon className="w-4 h-4 ml-1 rtl:ml-0 rtl:mr-1" />
            متصل ومحدث
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('dashboard.zatca_invoices')}
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {mudadData.zatcaStatus.invoicesIssued.toLocaleString()}
                </p>
              </div>
              <DocumentTextIcon className="w-8 h-8 text-blue-500" />
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  معدل الامتثال
                </p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {mudadData.zatcaStatus.complianceRate}%
                </p>
              </div>
              <ShieldCheckIcon className="w-8 h-8 text-green-500" />
            </div>
          </div>
        </div>
      </div>

      {/* Alerts Section */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">
            {t('dashboard.mudad_alerts')}
          </h3>
          <span className="bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 px-3 py-1 rounded-full text-sm font-medium">
            {mudadData.alerts.length} تنبيه
          </span>
        </div>

        <div className="space-y-4">
          {mudadData.alerts.map((alert) => (
            <motion.div
              key={alert.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className={`p-4 rounded-xl border ${getStatusBg(alert.type)}`}
            >
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  {alert.type === 'warning' ? (
                    <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />
                  ) : (
                    <CheckCircleIcon className="w-5 h-5 text-blue-500" />
                  )}
                </div>
                <div className="ml-3 rtl:ml-0 rtl:mr-3 flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {alert.message}
                  </p>
                  {alert.dueDate && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      مطلوب قبل: {new Date(alert.dueDate).toLocaleDateString('ar-SA')}
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default MudadCompliance;
